<script setup lang="ts">
    import { ChevronDown, Check, X, LoaderCircle } from 'lucide-vue-next';
    import {
        ComboboxAnchor,
        ComboboxContent,
        ComboboxInput,
        ComboboxItem,
        ComboboxLabel,
        ComboboxGroup,
        ComboboxEmpty,
        ComboboxRoot,
        ComboboxTrigger,
        ComboboxViewport,
        ComboboxSeparator,
        ComboboxItemIndicator,
        TagsInputInput,
        TagsInputItem,
        TagsInputItemDelete,
        TagsInputItemText,
        TagsInputRoot,
        useFilter,
    } from 'reka-ui';
    import { computed, ref } from 'vue';
    import type { RoleGroup, RoleOption } from '@/types';
    import { useI18n } from 'vue-i18n';

    interface Props {
        modelValue: RoleOption[];
        options: RoleGroup[];
        placeholder?: string;
        translate?: boolean;
        loading?: boolean;
        grouped?: boolean;
    }

    const props = withDefaults(defineProps<Props>(), {
        translate: false,
        placeholder: '',
        loading: false,
        grouped: false,
    });

    const emit = defineEmits<{
        'update:modelValue': [value: RoleOption[]];
    }>();

    const { t } = useI18n();
    const { contains } = useFilter({ sensitivity: 'base' });

    const searchTerm = ref('');
    const open = ref(false);
    const comboboxInputRef = ref<InstanceType<typeof ComboboxInput> | null>(null);

    const values = computed({
        get: () => props.modelValue,
        set: (newValues) => {
            emit('update:modelValue', newValues);
        },
    });

    const placeholderWithDefault = computed(
        () => props.placeholder || t('forms.select_placeholder'),
    );

    const filteredOptions = computed(() =>
        props.options
            .filter((group) => group.roles.some((role) => contains(role.name, searchTerm.value)))
            .map((group) => ({
                ...group,
                roles: group.roles.filter((role) => contains(role.name, searchTerm.value)),
            })),
    );

    const selectedRolesByCompany = computed(() => {
        const selectedMap = new Map<number, RoleOption>();
        values.value.forEach((role) => {
            selectedMap.set(role.company_id, role);
        });
        return selectedMap;
    });

    const isRoleDisabled = (role: RoleOption) => {
        return (
            selectedRolesByCompany.value.has(role.company_id) &&
            selectedRolesByCompany.value.get(role.company_id)?.id !== role.id
        );
    };

    const disabledStates = computed(() => {
        const disabledMap = new Map<RoleOption, boolean>();

        filteredOptions.value.forEach((group) => {
            group.roles.forEach((role) => {
                disabledMap.set(role, isRoleDisabled(role));
            });
        });

        return disabledMap;
    });

    const handleKeydown = (event: KeyboardEvent) => {
        if (event.key === 'Tab') {
            open.value = false;
        }
    };

    const handleInteractOutside = (event: any) => {
        if (event.target !== comboboxInputRef.value?.$el) {
            open.value = false;
        }
    };
</script>

<template>
    <ComboboxRoot
        v-model="values"
        v-model:open="open"
        :multiple="true"
        ignore-filter
        class="relative"
    >
        <ComboboxAnchor
            class="text-foreground data-[placeholder]:text-faded-foreground border-input flex min-h-7.5 w-full items-center justify-between gap-[5px] rounded-md border bg-white p-0.75 text-xs leading-none outline-none"
        >
            <TagsInputRoot
                v-model="values"
                delimiter=""
                class="flex flex-1 flex-wrap items-center gap-1 rounded-lg"
            >
                <TagsInputItem
                    v-if="!loading"
                    v-for="value in values"
                    :key="value.id"
                    :value="value"
                    class="bg-primary/10 aria-[current=true]:bg-primary text-foreground border-primary/20 flex items-center justify-center gap-2 rounded border px-1.5 py-0.5"
                >
                    <TagsInputItemText class="text-xs">
                        {{ value.name }}
                    </TagsInputItemText>
                    <TagsInputItemDelete>
                        <X class="size-3 cursor-pointer" />
                    </TagsInputItemDelete>
                </TagsInputItem>

                <ComboboxInput
                    ref="comboboxInputRef"
                    as-child
                    v-model="searchTerm"
                    @focus="
                        () => {
                            open = true;
                        }
                    "
                >
                    <TagsInputInput
                        :placeholder="placeholderWithDefault"
                        class="placeholder:text-faded-foreground h-5.5 flex-1 rounded !bg-transparent pl-1.75 focus:outline-none"
                        @keydown.enter.prevent
                        @keydown="handleKeydown"
                    />
                </ComboboxInput>
            </TagsInputRoot>

            <ComboboxTrigger class="shrink-0 pr-1.75">
                <ChevronDown
                    v-if="!loading"
                    class="text-foreground/50 size-4 cursor-pointer"
                />
                <LoaderCircle
                    v-else
                    class="text-foreground/50 ml-auto size-4 animate-spin"
                />
            </ComboboxTrigger>
        </ComboboxAnchor>

        <ComboboxContent
            class="data-[state=open]:animate-in data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[50% 0px] absolute z-10 mt-1 max-h-[200px] w-[var(--reka-popper-anchor-width)] overflow-y-auto rounded-md border bg-white p-1 shadow-md will-change-[opacity,transform]"
            @pointer-down-outside.prevent="handleInteractOutside"
            position="popper"
            align="start"
        >
            <ComboboxViewport>
                <template
                    v-for="(group, index) in filteredOptions"
                    :key="group.name"
                >
                    <ComboboxGroup class="my-2 flex flex-col gap-2 first:mt-0">
                        <ComboboxSeparator
                            v-if="index !== 0"
                            class="bg-border m-2 h-[1px]"
                        />
                        <ComboboxLabel class="px-2 text-xs font-bold">
                            {{ group.name }}
                        </ComboboxLabel>
                        <div class="grid grid-cols-3 gap-1">
                            <ComboboxItem
                                v-for="option in group.roles"
                                :key="`${option.id}-${disabledStates.get(option)}`"
                                class="data-[state=checked]:bg-primary/10 text-foreground data-[disabled]:text-faded-foreground data-[highlighted]:text-primary relative flex w-full cursor-pointer items-center gap-2 rounded-sm py-1.5 pr-2 pl-2 text-xs leading-none select-none data-[disabled]:pointer-events-none data-[highlighted]:outline-none data-[state=checked]:pr-8"
                                :value="option"
                                :disabled="disabledStates.get(option)"
                            >
                                <span>
                                    {{ option.name }}
                                </span>
                                <span
                                    class="absolute right-2 flex size-3.5 items-center justify-center"
                                >
                                    <ComboboxItemIndicator>
                                        <Check class="text-primary size-3" />
                                    </ComboboxItemIndicator>
                                </span>
                            </ComboboxItem>
                        </div>
                    </ComboboxGroup>
                </template>
                <ComboboxEmpty>
                    <span class="text-muted-foreground p-2 text-xs">{{
                        t('forms.no_options')
                    }}</span>
                </ComboboxEmpty>
            </ComboboxViewport>
        </ComboboxContent>
    </ComboboxRoot>
</template>
